// src/components/Layout.js
import React from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { LogOut, Home, Database, Star, List, Settings, Calculator as CalculatorIcon, Mail, CreditCard, Menu, Share2, Cpu, Package } from "lucide-react";
import { signOut } from "firebase/auth";
import { auth, db } from "../lib/firebase";
import { doc, getDoc, collection, query, where, getDocs, onSnapshot } from "firebase/firestore";
import logo from "../assets/naim-logo.png";
import { useUser } from "../context/UserProvider";
import { useTheme } from "../context/ThemeContext";
import { useTranslation } from "react-i18next";
import ConfirmModal from "./ConfirmModal";

export default function Layout() {
  const user = useUser();
  const { theme: t, isForest } = useTheme();
  const { t: tr } = useTranslation();
  const location = useLocation();
  const [counts, setCounts] = React.useState({ shared: 0, closedTickets: 0, sellRequests: 0 });
  const [refreshTrigger, setRefreshTrigger] = React.useState(0);
  const [sidebarOpen, setSidebarOpen] = React.useState(false); // mobile drawer
  const touchRef = React.useRef({ startX: 0, dragging: false, kind: null });
  const [signOutConfirmOpen, setSignOutConfirmOpen] = React.useState(false);

  // Toggle state for Boards/Converters in BuyingLists
  const [showBoards, setShowBoards] = React.useState(false);
  const isOnBuyingLists = location.pathname === '/buying-lists';

  // Listen for toggle state changes from BuyingLists component
  React.useEffect(() => {
    const handleToggleChange = (event) => {
      setShowBoards(event.detail.showBoards);
    };
    window.addEventListener('buyingListsToggleChange', handleToggleChange);
    return () => window.removeEventListener('buyingListsToggleChange', handleToggleChange);
  }, []);

  // Listen for badge refresh events
  React.useEffect(() => {
    const handleRefresh = () => setRefreshTrigger(prev => prev + 1);
    // Immediate clear of shared badge (no Firestore refetch) - used by SharedLists on mount
    const handleClearShared = () => setCounts(prev => ({ ...prev, shared: 0 }));
    window.addEventListener('refreshBadgeCounts', handleRefresh);
    window.addEventListener('clearSharedBadge', handleClearShared);
    return () => {
      window.removeEventListener('refreshBadgeCounts', handleRefresh);
      window.removeEventListener('clearSharedBadge', handleClearShared);
    };
  }, []);
  const handleToggle = () => {
    const newShowBoards = !showBoards;
    setShowBoards(newShowBoards);
    // Notify BuyingLists component
    window.dispatchEvent(new CustomEvent('headerToggleBuyingLists', { detail: { showBoards: newShowBoards } }));
  };

  const handleSignOut = async () => {
    try {
      await signOut(auth);
    } catch (e) {
      console.error("Sign out failed", e);
    }
  };

  // Real-time listener for sharedLists badge
  React.useEffect(() => {
    const uid = user?.uid;
    if (!uid) {
      setCounts({ shared: 0, closedTickets: 0, sellRequests: 0 });
      return;
    }

    let lastViewedTime = null;
    let viewedClosedTickets = {};

    // First get user settings, then set up real-time listener for sharedLists
    (async () => {
      try {
        const userDoc = await getDoc(doc(db, "users", uid));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          const lvs = userData.lastViewedSharedListsAt;
          if (lvs) {
            lastViewedTime = lvs.toDate ? lvs.toDate().getTime() : (lvs.seconds ? lvs.seconds * 1000 : lvs);
          }
          viewedClosedTickets = userData.viewedClosedTickets || {};
        }
        console.log('[Badge Debug] Initial lastViewedTime:', lastViewedTime);
      } catch { }

      // Fetch closed tickets and sell requests (one-time, these don't need real-time)
      try {
        const c = await getDoc(doc(db, "ticketsClosed", uid));
        const d = c.exists() ? c.data() || {} : {};
        const closed = Object.keys(d).filter(key => !viewedClosedTickets[key]).length;
        setCounts(prev => ({ ...prev, closedTickets: closed }));
      } catch { }

      try {
        const q = query(
          collection(db, "sellRequests"),
          where("uid", "==", uid),
          where("hasComment", "==", true)
        );
        const snap = await getDocs(q);
        setCounts(prev => ({ ...prev, sellRequests: snap.size }));
      } catch { }
    })();

    // Real-time listener for sharedLists
    const unsubShared = onSnapshot(doc(db, "sharedLists", uid), (snap) => {
      const d = snap.exists() ? snap.data() || {} : {};

      let shared = 0;
      if (lastViewedTime) {
        shared = Object.entries(d).filter(([id, list]) => {
          if (!list.date) return true;
          const listTime = typeof list.date === 'number' ? list.date : (list.date?.seconds ? list.date.seconds * 1000 : 0);
          const isNewer = listTime > lastViewedTime;
          return isNewer;
        }).length;
      } else {
        shared = Object.keys(d).length;
      }
      setCounts(prev => ({ ...prev, shared }));
    }, (err) => {
      console.error('[Layout] sharedLists onSnapshot error:', err);
    });

    return () => {
      unsubShared();
    };
  }, [user?.uid, refreshTrigger]);

  // Listen for badge refresh events from other pages (SharedLists, ContactUs, SellRequests)
  React.useEffect(() => {
    const handleRefresh = () => setRefreshTrigger(n => n + 1);
    const handleClearShared = () => setCounts(prev => ({ ...prev, shared: 0 }));
    const handleClearClosed = () => setCounts(prev => ({ ...prev, closedTickets: 0 }));
    const handleClearSellReqs = () => setCounts(prev => ({ ...prev, sellRequests: 0 }));

    window.addEventListener('refreshBadgeCounts', handleRefresh);
    window.addEventListener('clearSharedBadge', handleClearShared);
    window.addEventListener('clearClosedTicketsBadge', handleClearClosed);
    window.addEventListener('clearSellRequestsBadge', handleClearSellReqs);

    return () => {
      window.removeEventListener('refreshBadgeCounts', handleRefresh);
      window.removeEventListener('clearSharedBadge', handleClearShared);
      window.removeEventListener('clearClosedTicketsBadge', handleClearClosed);
      window.removeEventListener('clearSellRequestsBadge', handleClearSellReqs);
    };
  }, []);

  // Gesture to open from left edge on mobile (with visual drag)
  const onEdgeTouchStart = (e) => {
    if (sidebarOpen) return;
    const touch = e.touches?.[0];
    if (!touch) return;
    if (touch.clientX > 30) return; // left edge only
    touchRef.current = { startX: touch.clientX, dragging: true, kind: "open" };
  };
  const onEdgeTouchMove = (e) => {
    const st = touchRef.current;
    if (!st.dragging || st.kind !== "open") return;
    const touch = e.touches?.[0];
    if (!touch) return;
    const dx = touch.clientX - st.startX;
    if (dx > 60) {
      setSidebarOpen(true);
      st.dragging = false;
    }
  };
  const onEdgeTouchEnd = () => {
    touchRef.current.dragging = false;
  };

  // Swipe left on sidebar/overlay to close
  const closeSwipeStart = (e) => {
    const touch = e.touches?.[0];
    if (!touch) return;
    touchRef.current = { startX: touch.clientX, dragging: true, kind: "close" };
  };
  const closeSwipeMove = (e) => {
    const st = touchRef.current;
    if (!st.dragging || st.kind !== "close") return;
    const touch = e.touches?.[0];
    if (!touch) return;
    const dx = touch.clientX - st.startX;
    if (dx < -60) {
      setSidebarOpen(false);
      st.dragging = false;
    }
  };
  const closeSwipeEnd = () => {
    touchRef.current.dragging = false;
  };

  // Theme-based styles
  const mainBg = t.background.primary;
  const sidebarBg = isForest ? 'rgba(15, 58, 48, 0.95)' : t.navigation.background;
  const sidebarBorder = isForest ? 'rgba(255,255,255,0.1)' : t.navigation.border;
  const navTextColor = isForest ? '#e8f5e9' : t.text.primary;
  const navTextSecondary = isForest ? '#a5d6a7' : t.text.secondary;
  const topBarBg = isForest ? 'rgba(15, 58, 48, 0.95)' : 'rgba(255,255,255,0.95)';

  const navItemStyle = (isActive) => ({
    backgroundColor: isActive
      ? (isForest ? 'rgba(255,255,255,0.1)' : t.navigation.itemBackgroundActive)
      : 'transparent',
    color: isActive ? (isForest ? '#ffffff' : t.text.primary) : navTextSecondary,
    border: `1px solid ${isActive ? (isForest ? 'rgba(255,255,255,0.2)' : t.navigation.border) : 'transparent'}`,
  });

  return (
    <div className="min-h-screen" style={{ backgroundColor: mainBg, color: navTextColor }}>
      {/* Mobile overlay backdrop */}
      <div
        className={`md:hidden fixed inset-0 bg-black/50 transition-opacity z-40 ${sidebarOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}
        onClick={() => setSidebarOpen(false)}
        onTouchStart={closeSwipeStart}
        onTouchMove={closeSwipeMove}
        onTouchEnd={closeSwipeEnd}
      />

      {/* Sidebar (drawer on mobile, fixed on md+) */}
      <aside
        id="app-sidebar"
        className={`fixed inset-y-0 left-0 w-[260px] backdrop-blur-md z-50
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} md:translate-x-0`}
        style={{
          backgroundColor: sidebarBg,
          borderRight: `1px solid ${sidebarBorder}`,
          transition: 'transform 200ms ease-out',
        }}
        onTouchStart={sidebarOpen ? closeSwipeStart : undefined}
        onTouchMove={sidebarOpen ? closeSwipeMove : undefined}
        onTouchEnd={sidebarOpen ? closeSwipeEnd : undefined}
      >
        <div className="h-full min-h-0 flex flex-col overflow-hidden">
          {/* Logo */}
          <div className="pt-6 pb-4 flex justify-center">
            {/* Mobile: logo is a link to home; Desktop: plain image */}
            <NavLink to="/" className="md:hidden">
              <img src={logo} alt="Naim Web" className="h-12 w-auto" />
            </NavLink>
            <img src={logo} alt="Naim Web" className="hidden md:block h-12 w-auto" />
          </div>

          {/* Navigation */}
          <nav
            className="px-3 pt-2 space-y-1 md:space-y-2 text-lg md:text-xl font-semibold flex-1 overflow-visible md:overflow-auto"
            onClick={(e) => {
              // Close drawer after a selection on mobile
              try {
                const node = e.target;
                if (node && (node.closest('a') || node.closest('button'))) {
                  if (window.matchMedia && window.matchMedia('(max-width: 767px)').matches) {
                    setSidebarOpen(false);
                  }
                }
              } catch { }
            }}
          >
            <NavLink
              to="/"
              end
              className="flex items-center gap-4 rounded-xl px-3 py-2 md:py-3 transition-colors"
              style={({ isActive }) => navItemStyle(isActive)}
            >
              <Home size={22} />
              <span>{tr('navigation.home')}</span>
            </NavLink>

            <NavLink
              to="/converters"
              className="flex items-center gap-4 rounded-xl px-3 py-2 md:py-3 transition-colors"
              style={({ isActive }) => navItemStyle(isActive)}
            >
              <Database size={22} />
              <span>{tr('navigation.converters')}</span>
            </NavLink>

            {user?.gates?.navigation?.boards && (
              <NavLink
                to="/electronics"
                className="flex items-center gap-4 rounded-xl px-3 py-2 md:py-3 transition-colors"
                style={({ isActive }) => navItemStyle(isActive)}
              >
                <Cpu size={22} />
                <span>{tr('navigation.electronics')}</span>
              </NavLink>
            )}

            <NavLink
              to="/favorites"
              className="flex items-center gap-4 rounded-xl px-3 py-2 md:py-3 transition-colors"
              style={({ isActive }) => navItemStyle(isActive)}
            >
              <Star size={22} />
              <span>{tr('navigation.favorites')}</span>
            </NavLink>

            <NavLink
              to="/buying-lists"
              className="flex items-center gap-4 rounded-xl px-3 py-2 md:py-3 transition-colors"
              style={({ isActive }) => navItemStyle(isActive)}
            >
              <List size={22} />
              <span>{tr('navigation.buyingLists')}</span>
            </NavLink>

            {/* Shared Lists appears between Buying Lists and Calculator; gated by permissions */}
            {user?.gates?.navigation?.sharedLists && (
              <NavLink
                to="/shared-lists"
                className="flex items-center gap-4 rounded-xl px-3 py-2 md:py-3 transition-colors"
                style={({ isActive }) => navItemStyle(isActive)}
              >
                <Share2 size={22} />
                <span>{tr('navigation.sharedLists')}</span>
                {counts.shared > 0 && (
                  <span className="min-w-[20px] h-5 flex items-center justify-center px-1.5 rounded-full bg-red-500 text-white text-xs font-bold">
                    {counts.shared}
                  </span>
                )}
              </NavLink>
            )}

            {user?.gates?.navigation?.calculator && (
              <NavLink
                to="/calculator"
                className="flex items-center gap-4 rounded-xl px-3 py-2 md:py-3 transition-colors"
                style={({ isActive }) => navItemStyle(isActive)}
              >
                <CalculatorIcon size={22} />
                <span>{tr('navigation.calculator')}</span>
              </NavLink>
            )}
          </nav>

          {/* Bottom Section */}
          <div
            className="mt-auto px-3 pb-3 space-y-3 text-xl font-semibold"
            onClick={(e) => {
              try {
                const node = e.target;
                if (node && (node.closest('a') || node.closest('button'))) {
                  if (window.matchMedia && window.matchMedia('(max-width: 767px)').matches) {
                    setSidebarOpen(false);
                  }
                }
              } catch { }
            }}
          >
            <NavLink
              to="/sell-requests"
              className="flex items-center gap-4 rounded-xl px-3 py-2 md:py-3 transition-colors"
              style={({ isActive }) => navItemStyle(isActive)}
            >
              <Package size={22} />
              <span>{tr('navigation.sellRequest')}</span>
              {counts.sellRequests > 0 && (
                <span className="min-w-[20px] h-5 flex items-center justify-center px-1.5 rounded-full bg-red-500 text-white text-xs font-bold">
                  {counts.sellRequests}
                </span>
              )}
            </NavLink>

            <NavLink
              to="/subscription"
              className="flex items-center gap-4 rounded-xl px-3 py-2 md:py-3 transition-colors"
              style={({ isActive }) => navItemStyle(isActive)}
            >
              <CreditCard size={22} />
              <span>{tr('navigation.subscription')}</span>
            </NavLink>

            <NavLink
              to="/settings"
              className="flex items-center gap-4 rounded-xl px-3 py-2 md:py-3 transition-colors"
              style={({ isActive }) => navItemStyle(isActive)}
            >
              <Settings size={22} />
              <span>{tr('navigation.settings')}</span>
            </NavLink>

            <NavLink
              to="/contact-us"
              className="flex items-center gap-4 rounded-xl px-3 py-2 md:py-3 transition-colors"
              style={({ isActive }) => navItemStyle(isActive)}
            >
              <Mail size={22} />
              <span>{tr('navigation.contact')}</span>
              {counts.closedTickets > 0 && (
                <span className="min-w-[20px] h-5 flex items-center justify-center px-1.5 rounded-full bg-red-500 text-white text-xs font-bold">
                  {counts.closedTickets}
                </span>
              )}
            </NavLink>

            <button
              onClick={() => setSignOutConfirmOpen(true)}
              className="flex items-center gap-4 rounded-xl px-3 py-2 md:py-3 w-full text-left transition-colors hover:opacity-80"
              style={{ color: navTextSecondary }}
            >
              <LogOut size={22} />
              <span>{tr('auth.signOut')}</span>
            </button>

            <div className="pt-1 text-[11px]" style={{ color: isForest ? '#81c784' : t.text.tertiary }}>
              © {new Date().getFullYear()} Gebruder Naim
            </div>
          </div>
        </div>
      </aside>

      {/* Edge gesture zone (mobile)
          Note: Start below the sticky top bar to avoid Safari tap issues
          where a fixed transparent layer can intercept taps near the top-left. */}
      <div
        className="md:hidden fixed left-0 top-14 bottom-0 w-6 z-10"
        onTouchStart={onEdgeTouchStart}
        onTouchMove={onEdgeTouchMove}
        onTouchEnd={onEdgeTouchEnd}
      />

      {/* Main column */}
      <div className="md:pl-[260px] pl-0">
        {/* Mobile top bar with hamburger */}
        <div
          className="md:hidden sticky top-0 z-20 flex items-center gap-3 px-3 py-3 backdrop-blur supports-[backdrop-filter]:bg-opacity-75"
          style={{
            backgroundColor: topBarBg,
            borderBottom: `1px solid ${sidebarBorder}`,
          }}
        >
          <button
            type="button"
            aria-label="Open menu"
            aria-controls="app-sidebar"
            aria-expanded={sidebarOpen ? 'true' : 'false'}
            className="inline-flex items-center justify-center w-10 h-10 rounded-xl"
            style={{
              backgroundColor: isForest ? 'rgba(26,77,64,0.7)' : t.surface.card,
              border: `1px solid ${sidebarBorder}`,
              color: navTextColor,
            }}
            onClick={() => setSidebarOpen(true)}
          >
            <Menu size={20} />
          </button>
          {/* Mobile: logo clickable; Desktop: plain */}
          <NavLink to="/" className="md:hidden">
            <img src={logo} alt="Naim Web" className="h-8 w-auto" />
          </NavLink>
          <img src={logo} alt="Naim Web" className="hidden md:block h-8 w-auto" />
          <div id="topbar-actions" className="ml-auto flex items-center gap-2">
            {isOnBuyingLists && (
              <button
                onClick={handleToggle}
                className="px-3 py-2 rounded-lg text-sm font-medium"
                style={{
                  backgroundColor: isForest ? 'rgba(26,77,64,0.7)' : t.surface.card,
                  border: `1px solid ${sidebarBorder}`,
                  color: navTextColor,
                }}
              >
                {showBoards ? tr('navigation.converters') : tr('navigation.boards')}
              </button>
            )}
          </div>
        </div>

        <main className="p-4">
          <Outlet />
        </main>
      </div>

      {/* Sign Out Confirmation Modal */}
      <ConfirmModal
        open={signOutConfirmOpen}
        onClose={() => setSignOutConfirmOpen(false)}
        onConfirm={handleSignOut}
        title={tr('auth.signOut')}
        message={tr('auth.signOutConfirmMessage')}
        confirmLabel={tr('auth.signOut')}
        cancelLabel={tr('common.cancel')}
        destructive={false}
      />
    </div>
  );
}
